import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Envelope } from '@/components/Envelope';
import { MovieCard } from '@/components/MovieCard';
import { apiRequest, ApiError } from '@/lib/api';

interface AlternativeMovie {
  title: string;
  year: string;
  director: string;
  poster: string;
}

interface WatchProviderInfo {
  name: string;
  logoUrl: string;
  type: 'subscription' | 'rent' | 'buy' | 'free' | 'ads';
}

interface WatchProvidersData {
  providers: WatchProviderInfo[];
  tmdbLink: string;
  attribution: string;
}

interface MovieData {
  poster: string;
  director: string;
  synopsis: string;
  cast: string[];
  ratingImdb: string;
  ratingRt: string;
  quote: string;
  genre: string[];
  watchProviders?: WatchProvidersData;
}

interface RevealData {
  movieTitle: string;
  movieYear: string;
  movieData: MovieData;
  teaserText: string;
  alternatives: AlternativeMovie[];
  openedAt: string | null;
}

type LoadingState = 'loading' | 'error' | 'loaded';

export default function RevealPage() {
  const { token } = useParams<{ token: string }>();
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [revealData, setRevealData] = useState<RevealData | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    async function fetchRevealData() {
      if (!token) {
        setError('Invalid reveal link');
        setLoadingState('error');
        return;
      }

      try {
        const data = await apiRequest<RevealData>(`/reveal/${token}`);
        setRevealData(data);
        setLoadingState('loaded');

        // If already opened, skip envelope animation
        if (data.openedAt) {
          setIsRevealed(true);
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.getUserMessage());
        } else {
          setError(
            err instanceof Error ? err.message : 'An unexpected error occurred'
          );
        }
        setLoadingState('error');
      }
    }

    fetchRevealData();
  }, [token]);

  const handleEnvelopeOpen = () => {
    // Delay the reveal to let the envelope animation complete
    setTimeout(() => {
      setIsRevealed(true);
    }, 500);
  };

  if (loadingState === 'loading') {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-cream text-center">
          <div className="animate-pulse">
            <p className="font-serif text-xl italic">Loading your rental...</p>
          </div>
        </div>
      </div>
    );
  }

  if (loadingState === 'error') {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-cream font-display text-3xl mb-4">
            Something went wrong
          </h1>
          <p className="text-cream/60 font-serif">{error}</p>
        </div>
      </div>
    );
  }

  if (!revealData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-dark">
      {/* Envelope Stage */}
      {!isRevealed && (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Envelope
            posterUrl={revealData.movieData.poster}
            movieTitle={revealData.movieTitle}
            teaserText={revealData.teaserText}
            onOpen={handleEnvelopeOpen}
            isRevealed={isRevealed}
          />
        </div>
      )}

      {/* Movie Card Stage */}
      {isRevealed && (
        <div className="min-h-screen py-12 px-4">
          <MovieCard
            title={revealData.movieTitle}
            year={revealData.movieYear}
            movieData={revealData.movieData}
          />

          {/* Alternatives Section */}
          {revealData.alternatives && revealData.alternatives.length > 0 && (
            <div className="max-w-4xl mx-auto mt-12 animate-delayed-fade-in">
              <h2 className="text-cream font-display text-2xl mb-6 text-center">
                Other Ideas
              </h2>
              <div className="flex flex-wrap justify-center gap-4">
                {revealData.alternatives.map((alt, index) => (
                  <div
                    key={index}
                    className="w-36 md:w-44 bg-dark-800 rounded-lg overflow-hidden shadow-lg hover:scale-105 transition-transform"
                  >
                    <div
                      className="aspect-[2/3] bg-cover bg-center"
                      style={{ backgroundImage: `url(${alt.poster})` }}
                    />
                    <div className="p-3">
                      <h3 className="text-cream font-serif text-sm truncate">
                        {alt.title}
                      </h3>
                      <p className="text-cream/60 text-xs">
                        {alt.year} • {alt.director}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Log on Letterboxd Link */}
          <div className="max-w-4xl mx-auto mt-12 text-center animate-delayed-fade-in">
            <a
              href={`https://letterboxd.com/search/${encodeURIComponent(revealData.movieTitle + ' ' + revealData.movieYear)}/`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00e054] text-dark font-bold rounded-full hover:bg-[#00c049] transition-colors"
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 500 500"
                fill="currentColor"
              >
                <path d="M250 500C111.929 500 0 388.071 0 250S111.929 0 250 0s250 111.929 250 250-111.929 250-250 250zm-119.706-177.544c23.477 34.848 63.291 57.792 108.607 57.792s85.13-22.944 108.607-57.792c-23.477-34.848-63.291-57.792-108.607-57.792s-85.13 22.944-108.607 57.792z" />
              </svg>
              Log on Letterboxd
            </a>
          </div>

          {/* Footer */}
          <footer className="max-w-4xl mx-auto mt-16 pb-8 text-center animate-delayed-fade-in">
            <p className="text-cream/40 font-serif text-sm">
              <Link to="/" className="hover:text-cream/60 transition-colors">
                Still Love Film
              </Link>{' '}
              • Your weekly movie rental
            </p>
          </footer>
        </div>
      )}
    </div>
  );
}
